// every input/select with data-element="input"  will be iterated through to add to the endpoint url using the name="sort" value="name" for values in makeEndpointUrl()
// e.g `apiurl/?name=value&name2=value2`

// data-behaviour="endpoint-input" on change will take the value and do an api call regardless of other select/inputs values
// data-behaviour="go" on click will take makeEndpointUrl
// data-behaviour="go-to" on click will take the data-endpoint and do an api call  based on that regardless of other select/inputs values
// data-behaviour="clear-form" clears the form and getsResults
// data-behaviour="search" triggers after the 3rd character with a 500ms debounce. it also triggers if its changed to be empty
// data-element="input" on change triggers makeEndpointUrl() and resets the pagination to 1 if it isn't the pagination that was the change trigger

// data-behaviour="trigger-search" on select/input will trigger on change
import { _, debounce, makeGtmCall, debug } from '../utils.js'
import axios from 'axios'
import Loading from '../modules/loading.js'
import errorMessageContainer from '../templates/error.js'
import article from '../templates/article.js'
import download from '../templates/download.js'
import card from '../templates/card.js'
import cardAlt from '../templates/card-alt.js'
import search from '../templates/search.js'
import searchCategory from '../templates/search-category.js'
import LazyLoad from './lazy-load.js'

export default class Listing {
    constructor(el) {
        this.el = el
        this.form = _('form', 'element').nodeFrom(this.el)
        this.searchInput = _('input-search').nodeFrom(this.el)
        this.defaultErrorMessage =
            'Something has gone quite wrong, please try to refresh your browser or contact us.'
        this.pagination = _('pagination', 'element').nodeFrom(this.el)
        this.paginator = _('pagination-input').nodeFrom(this.el)
        this.previousPageTrigger = _('previous-page').nodeFrom(this.el)
        this.nextPageTrigger = _('next-page').nodeFrom(this.el)
        this.paginatorList = _('pagination-list', 'element').nodeFrom(this.el)
        this.totalPages = _('total-pages', 'element').nodeFrom(this.el)
        this.term = _('term', 'element').nodeFrom(this.el)
        this.searchPanel = _('search-panel', 'element').nodeFrom(this.el)
        this.inputs = _('input', 'element').nodesFrom(this.el)
        this.action = this.el.dataset.action || 'get'
        this.resultsContainer = _('results-container', 'element').nodeFrom(
            this.el,
        )
        this.searchCategories = _('search-categories', 'element').nodeFrom(
            this.el,
        )
        this.endpointInput = _('endpoint-input').nodeFrom(this.el)
        this.noResultsContainer = _('no-results', 'element').nodeFrom(this.el)
        this.goTos = _('go-to').nodesFrom(this.el)
        this.gos = _('go').nodesFrom(this.el)
        this.clearFormTrigger = _('clear-form').nodeFrom(this.el)
        this.urlCheck = this.el.dataset.urlCheck || 'true'
        this.onLoad = this.el.dataset.onload || 'true'
        this.trendingSearchLabels = _('trending-search-label').nodesFrom(
            this.el,
        )

        // Autocomplete properties
        this.suggestEndpoint = this.searchInput?.dataset.suggestEndpoint || null
        this.suggestMinChars = parseInt(this.searchInput?.dataset.suggestMinChars, 10) || 2
        this.pageId = this.getPageIdFromForm() // Get page ID from hidden input in form
        this.currentSuggestion = null
        this.suggestionOverlay = null

        this.endpointInput.addEventListener('change', this.getResults)
        this.form.addEventListener('submit', this.formSubmit)

        this.goTos.map((goto) => {
            goto.addEventListener('click', this.setEndpointUrl)
        })
        this.gos.map((go) => {
            go.addEventListener('click', this.makeEndpointUrl)
        })
        this.clearFormTrigger.addEventListener('click', this.clearForm)

        this.trendingSearchLabels.map((label) => {
            label.addEventListener('click', this.handleTrendingSearchClick)
        })
        this.scrollToContainer = false

        if (this.previousPageTrigger) {
            this.previousPageTrigger.addEventListener(
                'click',
                this.goPreviousPage,
            )
        }
        if (this.nextPageTrigger) {
            this.nextPageTrigger.addEventListener('click', this.goNextPage)
        }

        this.inputs.map((input) => {
            input.addEventListener('change', this.makeEndpointUrl)
        })
        if (this.searchInput) {
            this.searchInput.addEventListener(
                'keyup',
                debounce(this.search, 500),
            )
            this.searchInput.addEventListener(
                'input',
                this.catchSearchInputCancel,
            )

            // Add autocomplete functionality
            if (this.suggestEndpoint) {
                this.setupAutocomplete()
            }
        }

        if (this.urlCheck === 'true') {
            this.changeInputsToParams()
        }

        if (this.searchInput) {
            if (this.urlCheck === 'true' && this.searchInput.value.length > 0) {
                this.getResults(false, false)
            }
        }
    }

    catchSearchInputCancel = () => {
        if (this.searchInput.value.length === 0) {
            this.makeEndpointUrl()
        } else {
            return false
        }
    }

    changeInputsToParams = () => {
        const url = new URL(window.location)
        const params = new URLSearchParams(url.search)
        let paramsPresent = false

        params.forEach((value, key) => {
            if (value.includes('"') && typeof value === 'string') {
                value = value.replaceAll('"', '')
            }
            if (key === 'null' || key === 'modal' || value === 'null') {
                if (debug) {
                    console.warn('you have a null key/value')
                }
                return
            }

            const regex = /^[a-z0-9-_(),&\s]+$/i
            if (!regex.test(key) || !regex.test(value)) {
                if (debug) {
                    console.warn('you have a non-alphanumeric key/value')
                }
                return
            }

            paramsPresent = true
            const input = this.el.querySelector(`[name="${key}"]`)
            const radioInput = this.el.querySelector(`[value="${value}"]`)

            // small catch for if you land directly on search the HTML doesn't exist yet
            if (input === null) {
                return
            }

            if (input && input.type === 'checkbox') {
                input.checked = value === 'true'
            }

            if (input && input.type === 'radio') {
                radioInput.checked = 'true'
            }

            if (
                (input && input.type === 'text') ||
                input.type === 'number' ||
                input.type === 'select-one' ||
                input.type === 'search' ||
                input.type === 'hidden'
            ) {
                // Decode URL-encoded values when setting them in inputs
                input.value = decodeURIComponent(value)
            }
        })

        if (paramsPresent) {
            return this.makeEndpointUrl()
        }

        if (!paramsPresent && this.onLoad === 'true') {
            this.makeEndpointUrl()
        }
    }

    formSubmit = (e) => {
        e.preventDefault()
    }

    showLoading = () => {
        this.noResultsContainer.classList.add('u-hidden')
        this.searchInput?.scrollIntoView({ behavior: "smooth", block: "start" });
        this.resultsContainer.innerHTML =
            '<div class="loading" data-behaviour="loading"></div>'
        _('loading')
            .nodes()
            .map((el) => new Loading(el))
    }

    hideLoading = () => {
        this.resultsContainer.innerHTML = ''
    }

    search = (e) => {
        if (e.target.value.length >= 2 || e.target.value.length === 0) {
            this.paginator.value = 1
            this.makeEndpointUrl()
        }
    }

    clearForm = () => {
        if (this.el.dataset.type !== 'search') {
            this.form.reset()
        }
        const tab = _('toggle-select').nodeFrom(this.form)
        const selects = [...this.form.querySelectorAll('select')]
        const radioCategory = this.form.querySelector(
            '[name="category"][value="All"]',
        )
        if (tab) {
            _('toggle-select').node().checked = true
        }
        if (selects) {
            selects.map((select) => {
                select.selectedIndex = 0
            })
        }
        if (radioCategory) {
            radioCategory.checked = true
        }

        this.makeEndpointUrl()
    }

    setEndpointUrl = (e) => {
        this.endpointInput.value = e.currentTarget.dataset.endpoint
        this.getResults()
    }

    resetPaginator = () => {
        if (this.paginator) {
            this.paginator.value = 1
        }
    }

    getPageIdFromForm = () => {
        // Look for hidden input with name="pageId" in the form
        const pageIdInput = this.form?.querySelector('input[name="pageId"][type="hidden"]')
        return pageIdInput ? pageIdInput.value : null
    }

    // Only add AWS WAF token if it exists (for production environments)
    getAwsWafToken = () => {
        // Check for token in cookie first (most common for initial requests)
        const cookieMatch = document.cookie.match(/aws-waf-token=([^;]+)/)
        if (cookieMatch && cookieMatch[1]) {
            return cookieMatch[1]
        }

        return null
    }

    getHeaders = () => {
        const headers = {
            'User-Agent': navigator.userAgent,
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': navigator.language || 'en-US',
            'X-Requested-With': 'XMLHttpRequest',
            'X-Bypass-WAF': true,
        }

        const awsWafToken = this.getAwsWafToken()
        if (awsWafToken) {
            headers['x-aws-waf-token'] = awsWafToken
        }

        return headers
    }

    makeEndpointUrl = (e) => {
        if (
            e &&
            e.currentTarget &&
            e.currentTarget.dataset.behaviour !== 'pagination'
        ) {
            this.resetPaginator()
        }

        // don't trigger on search change
        if (
            e &&
            e.currentTarget.dataset &&
            e.currentTarget.dataset.behaviour === 'input-search' &&
            this.searchInput.value.length !== 0
        ) {
            return false
        }

        this.startEndpoint = this.endpointInput.value.split('?')
        this.endpoint = this.startEndpoint[0]

        // Always process form inputs if there's a search value or if term display is populated
        if (
            (this.searchInput && this.searchInput.value.trim() !== '') ||
            this.term?.innerHTML !== ''
        ) {
            const inputs = _('input', 'element').nodesFrom(this.el)
            if (inputs.length) {
                inputs.map((el, i) => {
                    if (!i) {
                        this.endpoint += '?'
                    }
                    const name = el.getAttribute('name')
                    if (el.type !== 'radio' && el.type !== 'checkbox' && name) {
                        // Only encode the value for URL, not for display
                        this.endpoint += `&${name}=${encodeURIComponent(el.value)}`
                    }
                    if (el.type === 'radio' && el.checked && name) {
                        this.endpoint += `&${name}=${encodeURIComponent(el.value)}`
                    }
                    if (el.type === 'checkbox' && el.checked && name) {
                        this.endpoint += `&${name}=${el.checked}`
                    }
                })
            }
        } else {
            this.endpoint += decodeURIComponent(window.location.search)
        }

        if (this.pagination && !this.paginator.value) {
            this.endpoint += `&${this.paginator.getAttribute('name')}=1`
        }
        this.endpointInput.value = encodeURI(this.endpoint)

        if (
            !!e &&
            !!e.currentTarget &&
            e.currentTarget.dataset.behaviour === 'pagination'
        ) {
            this.getResults(true)
        } else {
            this.resetPaginator()
            this.getResults(false)
        }
    }

    getResults = (scrollTop = false, changeParams = true) => {
        if (
            this.el.dataset.type === 'search' &&
            this.searchInput.value.length < 2
        ) {
            return false
        }
        this.showLoading()

        const headers = this.getHeaders()

        axios({
            method: this.action,
            url: this.endpointInput.value,
            headers: headers,
            timeout: 30000, // 30 second timeout
            withCredentials: true, // Include cookies in requests
        })
            .then((response) => {
                if (this.pagination) {
                    this.buildPagination(response.data)
                }
                if (response.status !== 200) {
                    return this.handleError(response)
                }

                if (!response.data.data || !response.data.data.length) {
                    if (this.el.dataset.type === 'search') {
                        this.makeSearchCategories(response.data.categories)
                        return this.handleNoResults(
                            response.data.categories &&
                                response.data.categories[0]
                                ? response.data.categories[0].results
                                : 0,
                        )
                    }
                    return this.handleNoResults()
                }
                this.changeParams(changeParams)
                this.handleSuccess(response.data)
                if (this.scrollToContainer) {
                    this.scrollToResultsTop()
                }
            })
            .catch((error) => {
                this.handleError(error)
            })
    }

    changeParams = (changeParams = true) => {
        if (this.urlCheck === 'true') {
            const url = new URL(window.location)
            const params = new URLSearchParams(url.search)

            if (!changeParams && params.size > 0) {
                return
            }
            let modalParam = ''
            if (params.has('modal')) {
                modalParam = `&modal=${params.get('modal')}`
            }

            window.history.replaceState(
                null,
                null,
                `${window.location.pathname}?${
                    this.endpointInput.value.split('?')[1]
                }${modalParam}${window.location.hash}`,
            )
        }
    }

    scrollToResultsTop = async () => {
        console.log('scrolling to top of results')
        window.scrollTo({top: window.scrollY + document.querySelector('.input-search').getBoundingClientRect().top || 0 , behavior: 'smooth'})
        console.log('scrolled to top of results')
        this.scrollToContainer = false
    }

    goNextPage = () => {
        this.paginator.value++
        this.makeEndpointUrl()
        this.scrollToContainer = true
    }

    goPreviousPage = () => {
        this.paginator.value--

        this.makeEndpointUrl()
        this.scrollToContainer = true

    }

    changePaginationInput = (event) => {
        this.paginator.value = event.target.value
        this.makeEndpointUrl()
        this.scrollToContainer = true

    }

    buildPagination = (response) => {
        if (!response) {
            return false
        }
        if (
            !response.data.length ||
            (response.pagination &&
                response.pagination.totalPages !== null &&
                response.pagination.totalPages < 2)
        ) {
            this.pagination.classList.add('u-hidden')
            return false
        } else {
            this.pagination.classList.remove('u-hidden')
        }
        const totalPages =
            response.pagination?.totalPages ?? response.total_pages
        const currentPage = response.pagination?.currentPage ?? response.page
        let paginationHTML = ''

        const isInRange = (i) => Math.abs(currentPage - i) <= 2

        // Display one empty button before the range if page 1 is not in range
        if (currentPage > 3) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link" value="1" data-behaviour="pagination-button">
            1
        </button>
    </li>`
        }

        // Display one empty button before the range
        if (currentPage > 3) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link pagination__link--no-border" disabled>
            ...
        </button>
    </li>`
        }

        // Display buttons within the range
        for (let i = 1; i <= totalPages; i++) {
            const isCurrentPage = i === currentPage

            if (isCurrentPage || isInRange(i)) {
                paginationHTML += `<li class="pagination__item ${
                    isCurrentPage ? 'pagination__item--active' : ''
                }">
            <button class="pagination__link" value="${i}" data-behaviour="pagination-button">
                ${i}
                ${isCurrentPage ? '<span class="sr-only">current</span>' : ''}
            </button>
        </li>`
            }
        }

        // Display one empty button after the range
        if (currentPage < totalPages - 2) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link pagination__link--no-border" disabled>
            ...
        </button>
    </li>`
        }

        // Display one empty button after the range if the last page is not in range
        if (currentPage < totalPages - 2) {
            paginationHTML += `<li class="pagination__item">
        <button class="pagination__link" value="${totalPages}" data-behaviour="pagination-button">
            ${totalPages}
        </button>
    </li>`
        }

        this.paginatorList.innerHTML = paginationHTML
        this.totalPages.value = response.pagination.totalPages
        this.paginator.value = response.pagination.currentPage

        if (this.previousPageTrigger) {
            this.previousPageTrigger.disabled = currentPage === 1
        }
        if (this.nextPageTrigger) {
            this.nextPageTrigger.disabled = currentPage === totalPages
        }

        _('pagination-button')
            .nodesFrom(this.el)
            .forEach((buttonElement) => {
                buttonElement.addEventListener('click', (event) => {
                    this.changePaginationInput(event)
                })
            })
    }

    handleSuccess = (response) => {
        this.hideLoading()
        this.toggleSearchPanel(true)

        if (this.el.dataset.type === 'article') {
            return this.handleSuccessTypeArticle(response)
        }
        if (this.el.dataset.type === 'download') {
            return this.handleSuccessTypeLegal(response)
        }
        if (this.el.dataset.type === 'card') {
            return this.handleSuccessTypeCard(response, card)
        }
        if (this.el.dataset.type === 'card-alt') {
            return this.handleSuccessTypeCard(response, cardAlt)
        }
        if (this.el.dataset.type === 'search') {
            this.makeSearchCategories(response.categories)
            return this.handleSuccessTypeSearch(response)
        }
        this.searchInput?.scrollIntoView({ behavior: "smooth", block: "start" });

        return this.handleError('missing data type')
    }

    toggleSearchPanel = (active = false) => {
        if (this.searchPanel) {
            if (active) {
                this.term.innerHTML = this.searchInput.value
                this.searchPanel.classList.remove('u-hidden')
            } else {
                this.term.innerHTML = ''
                this.searchPanel.classList.add('u-hidden')
            }
        }
    }

    handleSuccessTypeSearch = (response) => {
        let resultsHTML = `<p class="u-mb-30">Displaying result${
            response.pagination.totalResults > 1 ? 's' : ''
        } ${response.pagination.itemFirst} - ${
            response.pagination.itemLast
        } of ${response.pagination.totalResults}</p>`
        if (response.data) {
            response.data.map((el, i) => {
                const item = search(response.data[i])
                resultsHTML += item
            })
        }
        this.resultsContainer.innerHTML = resultsHTML
        this.reEnableModules()
    }

    makeSearchCategories = (categories) => {
        let urlToUse
        try {
            // Check if endpointInput.value is already a full URL
            if (this.endpointInput.value.startsWith('http')) {
                urlToUse = new URL(this.endpointInput.value)
            } else {
                // If it's a relative path, construct the full URL
                const path = this.endpointInput.value.startsWith('/')
                    ? this.endpointInput.value
                    : '/' + this.endpointInput.value
                urlToUse = new URL(window.location.origin + path)
            }
        } catch (error) {
            console.warn(
                'Failed to construct URL for search categories:',
                error,
            )
            // Fallback to current window location
            urlToUse = new URL(window.location.href)
        }

        const params = new URLSearchParams(urlToUse.search)
        const selectedCategory = params.get('category') || 'All'

        let resultsHTML = ''
        if (categories) {
            categories.map((el, i) => {
                const item = searchCategory(i, categories[i], selectedCategory)
                resultsHTML += item
            })
        }
        this.searchCategories.innerHTML = resultsHTML

        const inputs = _('input', 'element').nodesFrom(this.searchCategories)

        inputs.map((input) => {
            input.addEventListener('change', this.makeEndpointUrl)
        })
    }

    handleSuccessTypeLegal = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--equal-height grid--break-2@large grid--break@medium">'
            response.data.map((el, i) => {
                const item = download(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        this.resultsContainer.innerHTML = resultsHTML
        this.reEnableModules()
    }

    handleSuccessTypeCard = (response, template) => {
        let resultsHTML = ''

        if (response.featured) {
            resultsHTML +=
                '<div class="grid grid--3-col grid--equal-height grid--break@medium u-mb">'
            response.featured.map((el, i) => {
                const item = template(response.featured[i], 'card--big u-mb')
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--equal-height grid--break-2@large grid--break@medium">'
            response.data.map((el, i) => {
                const item = template(response.data[i], 'card--small u-mb')
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        this.resultsContainer.innerHTML = resultsHTML
        this.reEnableModules()
    }

    handleSuccessTypeArticle = (response) => {
        let resultsHTML = ''
        if (response.featured) {
            resultsHTML +=
                '<div class="grid grid--3-col grid--equal-height grid--break@medium u-mb0">'
            response.featured.map((el, i) => {
                const item = article(i, response.featured[i], 'article--large')
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--equal-height grid--break-2@large grid--break@small">'
            response.data.map((el, i) => {
                const item = article(i, response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }
        this.resultsContainer.innerHTML = resultsHTML
        _('image', 'lazyload')
            .nodes()
            .map((el) => new LazyLoad(el))
    }

    reEnableModules = () => {
        _('gtm-click')
            .nodesFrom(this.resultsContainer)
            .map((el) => {
                el.addEventListener('click', () => {
                    makeGtmCall(el)
                })
            })
    }

    handleNoResults = (allCategoriesNo) => {
        if (allCategoriesNo === 0) {
            this.clearFormTrigger.classList.add('u-hidden')
        } else {
            this.clearFormTrigger.classList.remove('u-hidden')
        }
        this.toggleSearchPanel(true)
        this.noResultsContainer.classList.remove('u-hidden')
        this.hideLoading()
    }

    handleError = (err, defaultMessage = this.defaultErrorMessage) => {
        console.warn(err)
        this.hideLoading()
        this.toggleSearchPanel(false)
        let errorHTML = `<p>${defaultMessage}</p>`
        if (err && err.errors) {
            err.errors.map((el) => {
                errorHTML += `<p>${el}</p>`
            })
        }
        this.resultsContainer.innerHTML = errorMessageContainer(errorHTML)
    }

    setupAutocomplete = () => {
        // Create suggestion dropdown element
        this.createSuggestionOverlay()

        // Add event listeners for autocomplete
        this.searchInput.addEventListener(
            'input',
            debounce(this.handleSuggestionInput, 300),
        )
        this.searchInput.addEventListener(
            'keydown',
            this.handleSuggestionKeydown,
        )
        this.searchInput.addEventListener('blur', () => {
            // Delay hiding to allow clicks on suggestions
            setTimeout(() => this.hideSuggestions(), 150)
        })
    }

    createSuggestionOverlay = () => {
        // Create a wrapper div specifically for positioning the input and dropdown
        const inputWrapper = document.createElement('div')
        inputWrapper.style.position = 'relative'
        inputWrapper.style.display = 'inline-block'
        inputWrapper.style.width = '100%'

        // Insert wrapper before the input and move input inside it
        this.searchInput.parentNode.insertBefore(inputWrapper, this.searchInput)
        inputWrapper.appendChild(this.searchInput)

        // Get the input's computed margin to position dropdown correctly
        const inputStyles = window.getComputedStyle(this.searchInput)
        const marginBottom = inputStyles.marginBottom

        // Create dropdown suggestion list
        this.suggestionDropdown = document.createElement('div')
        this.suggestionDropdown.className = 'suggestion-dropdown'
        this.suggestionDropdown.style.cssText = `
            position: absolute;
            top: calc(100% - ${marginBottom});
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #ddd;
            border-top: none;
            border-radius: 0 0 4px 4px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            z-index: 1000;
            max-height: 300px;
            overflow-y: auto;
            display: none;
        `

        // Add the dropdown to the input wrapper
        inputWrapper.appendChild(this.suggestionDropdown)

        // Initialize dropdown state
        this.selectedSuggestionIndex = -1
        this.suggestions = []
    }

    handleSuggestionInput = (e) => {
        const query = e.target.value.trim()

        if (query.length >= this.suggestMinChars) {
            this.fetchSuggestions(query)
        } else {
            this.hideSuggestions()
        }
    }

    fetchSuggestions = async (query) => {
        try {
            const headers = this.getHeaders()

            // Build URL with query parameter and optional page ID
            let suggestionUrl = `${this.suggestEndpoint}?q=${encodeURIComponent(query)}`

            // Add page ID parameter if it exists
            if (this.pageId) {
                suggestionUrl += `&pageId=${encodeURIComponent(this.pageId)}`
            }

            const response = await axios.get(suggestionUrl, {
                headers: headers,
                timeout: 10000,
                withCredentials: true,
            })

            if (
                response.data &&
                response.data.suggestions &&
                response.data.suggestions.length > 0
            ) {
                // Limit to maximum 5 suggestions
                this.suggestions = response.data.suggestions.slice(0, 5)
                this.showSuggestions(query)
            } else {
                this.hideSuggestions()
            }
        } catch (error) {
            console.warn('Suggestion fetch failed:', error)
            this.hideSuggestions()
        }
    }

    showSuggestions = (query) => {
        if (!this.suggestions.length) {
            this.hideSuggestions()
            return
        }

        let dropdownHTML = ''
        this.suggestions.forEach((suggestion, index) => {
            // Highlight the matching query part
            const lowerSuggestion = suggestion.toLowerCase()
            const lowerQuery = query.toLowerCase()
            const matchIndex = lowerSuggestion.indexOf(lowerQuery)

            let displayText = suggestion
            if (matchIndex !== -1) {
                const beforeMatch = suggestion.substring(0, matchIndex)
                const match = suggestion.substring(
                    matchIndex,
                    matchIndex + query.length,
                )
                const afterMatch = suggestion.substring(
                    matchIndex + query.length,
                )
                displayText = `${beforeMatch}<strong>${match}</strong>${afterMatch}`
            }

            dropdownHTML += `
                <div class="suggestion-item" data-suggestion="${suggestion}" data-index="${index}" style="
                    padding: 10px 15px;
                    cursor: pointer;
                    border-bottom: 1px solid #f0f0f0;
                    transition: background-color 0.2s;
                " onmouseover="this.style.backgroundColor='#f8f9fa'" onmouseout="this.style.backgroundColor='white'">
                    ${displayText}
                </div>
            `
        })

        this.suggestionDropdown.innerHTML = dropdownHTML
        this.suggestionDropdown.style.display = 'block'
        this.selectedSuggestionIndex = -1

        // Add click event listeners to suggestion items
        this.suggestionDropdown
            .querySelectorAll('.suggestion-item')
            .forEach((item, index) => {
                item.addEventListener('click', () => {
                    this.selectSuggestion(item.dataset.suggestion)
                })
            })
    }

    hideSuggestions = () => {
        if (this.suggestionDropdown) {
            this.suggestionDropdown.style.display = 'none'
            this.suggestionDropdown.innerHTML = ''
        }
        this.suggestions = []
        this.selectedSuggestionIndex = -1
    }

    selectSuggestion = (suggestion) => {
        this.searchInput.value = suggestion
        this.hideSuggestions()

        // Trigger search
        this.resetPaginator()
        this.makeEndpointUrl()
    }

    handleSuggestionKeydown = (e) => {
        if (
            !this.suggestions.length ||
            this.suggestionDropdown.style.display === 'none'
        ) {
            return
        }

        const suggestionItems =
            this.suggestionDropdown.querySelectorAll('.suggestion-item')

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                this.selectedSuggestionIndex = Math.min(
                    this.selectedSuggestionIndex + 1,
                    this.suggestions.length - 1,
                )
                this.updateSelectedSuggestion(suggestionItems)
                break

            case 'ArrowUp':
                e.preventDefault()
                this.selectedSuggestionIndex = Math.max(
                    this.selectedSuggestionIndex - 1,
                    -1,
                )
                this.updateSelectedSuggestion(suggestionItems)
                break

            case 'Enter':
                if (this.selectedSuggestionIndex >= 0) {
                    e.preventDefault()
                    this.selectSuggestion(
                        this.suggestions[this.selectedSuggestionIndex],
                    )
                }
                break

            case 'Escape':
                this.hideSuggestions()
                break

            case 'Tab':
                if (this.selectedSuggestionIndex >= 0) {
                    e.preventDefault()
                    this.selectSuggestion(
                        this.suggestions[this.selectedSuggestionIndex],
                    )
                } else if (this.suggestions.length > 0) {
                    e.preventDefault()
                    this.selectSuggestion(this.suggestions[0])
                }
                break
        }
    }

    updateSelectedSuggestion = (suggestionItems) => {
        // Remove highlight from all items
        suggestionItems.forEach((item) => {
            item.style.backgroundColor = 'white'
        })

        // Highlight selected item
        if (this.selectedSuggestionIndex >= 0) {
            suggestionItems[
                this.selectedSuggestionIndex
            ].style.backgroundColor = '#f8f9fa'
        }
    }

    handleTrendingSearchClick = (e) => {
        const term = e.currentTarget.dataset.term
        if (term) {
            this.searchInput.value = term
            this.resetPaginator()
            this.makeEndpointUrl()
        }
    }
}
